import React, { useMemo, useState } from "react";

import {
  Banner,
  Button,
  Card,
  Modal,
  ResourceItem,
  ResourceList,
  Stack,
  TextField,
  TextStyle,
} from "@shopify/polaris";

import ImportGroupingPickerModal from "./ImportGroupingPickerModal";
import WeightDescriptorEditor, {
  descriptorsAreValid,
} from "./WeightDescriptorEditor";
import {
  cleanGroupingOverrideForSave,
  normalizeGroupingKey,
} from "./weightOverridePayloads";

/**
 * Category default weights section for the Weight Options page.
 *
 * Mirrors `SubdepartmentVariantWeightsSection` with three deltas:
 *   - Picker opens with mode='category'.
 *   - Saved payload writes to `globepos_category_variant_weights`.
 *   - Editor exposes an optional `parent_subdepartment` field (display hint
 *     only — the tracker doesn't pair sub-dept + category in v1, so this is
 *     populated by manual entry today).
 *
 * Per spec: category matches use the raw `CATEGORY DESCRIPTION` value from
 * ITEMFILE — values are uppercased on the backend before storage; this UI
 * preserves that casing.
 *
 * Props
 * -----
 * @param {object}   shopConfig - The shop_config object.  Reads
 *                                `globepos_category_variant_weights`;
 *                                missing or non-object values are treated as {}.
 * @param {number}   shopId     - Used by the picker's fetch.
 * @param {function} onSave     - Called with a partial shop_config object
 *                                (always under the key
 *                                `globepos_category_variant_weights`).
 *                                May return a Promise.
 */
export default function CategoryVariantWeightsSection({
  shopConfig,
  shopId,
  onSave,
}) {
  const overrides = useMemo(() => {
    const raw = shopConfig && shopConfig.globepos_category_variant_weights;
    return raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
  }, [shopConfig]);

  const entries = useMemo(
    () =>
      Object.entries(overrides)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, value]) => ({
          key,
          label: (value && value.label) || key,
          code: (value && value.code) || null,
          parent_subdepartment:
            (value && value.parent_subdepartment) || null,
          descriptors: Array.isArray(value && value.descriptors)
            ? value.descriptors
            : [],
        })),
    [overrides],
  );

  // ---- Picker state ---------------------------------------------------------
  const [pickerOpen, setPickerOpen] = useState(false);

  // ---- Editor state ---------------------------------------------------------
  const [activeEntry, setActiveEntry] = useState(null);
  const [editorLabel, setEditorLabel] = useState("");
  const [editorParent, setEditorParent] = useState("");
  const [editorDescriptors, setEditorDescriptors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // ---- Remove confirmation state -------------------------------------------
  const [confirmingDeleteKey, setConfirmingDeleteKey] = useState(null);

  // ---- Picker → editor wiring ----------------------------------------------
  const handlePickerSelect = ({ key, label, code, parent_subdepartment }) => {
    setPickerOpen(false);
    const existing = overrides[key];
    setActiveEntry({
      key,
      label: label || key,
      code: code || (existing && existing.code) || null,
      parent_subdepartment:
        parent_subdepartment ||
        (existing && existing.parent_subdepartment) ||
        null,
      descriptors:
        existing && Array.isArray(existing.descriptors)
          ? existing.descriptors
          : [{ weight: "", description: "" }],
      isNew: !existing,
    });
    setEditorLabel(existing ? existing.label || label || key : label || key);
    setEditorParent(
      (existing && existing.parent_subdepartment) ||
        parent_subdepartment ||
        "",
    );
    setEditorDescriptors(
      existing && Array.isArray(existing.descriptors) && existing.descriptors.length > 0
        ? existing.descriptors.map((d) => ({
            weight: String(d.weight || ""),
            description: String(d.description || ""),
          }))
        : [{ weight: "", description: "" }],
    );
    setSaving(false);
    setSaveError(null);
  };

  const handleEditEntry = (entry) => {
    setActiveEntry({
      key: entry.key,
      label: entry.label,
      code: entry.code,
      parent_subdepartment: entry.parent_subdepartment,
      descriptors: entry.descriptors,
      isNew: false,
    });
    setEditorLabel(entry.label);
    setEditorParent(entry.parent_subdepartment || "");
    setEditorDescriptors(
      entry.descriptors.length > 0
        ? entry.descriptors.map((d) => ({
            weight: String(d.weight || ""),
            description: String(d.description || ""),
          }))
        : [{ weight: "", description: "" }],
    );
    setSaving(false);
    setSaveError(null);
  };

  const closeEditor = () => {
    if (saving) return;
    setActiveEntry(null);
    setEditorLabel("");
    setEditorParent("");
    setEditorDescriptors([]);
    setSaveError(null);
  };

  // ---- Save flow -----------------------------------------------------------
  const normalizedEditorKey = normalizeGroupingKey(
    editorLabel || (activeEntry && activeEntry.key) || "",
  );
  const editorKeyIsBlank = normalizedEditorKey === "";
  const descriptorsValid = descriptorsAreValid(editorDescriptors);
  const canSave = !editorKeyIsBlank && descriptorsValid;

  const handleSaveEditor = () => {
    if (!canSave) return;
    setSaveError(null);

    const [key, payload] = cleanGroupingOverrideForSave({
      key: activeEntry.key || editorLabel,
      label: editorLabel,
      code: activeEntry.code,
      parent_subdepartment: editorParent,
      descriptors: editorDescriptors,
      includeParent: true,
    });

    const previousKey = activeEntry.isNew ? null : activeEntry.key;
    const nextOverrides = { ...overrides };
    if (previousKey && previousKey !== key) {
      delete nextOverrides[previousKey];
    }
    nextOverrides[key] = payload;

    const result = onSave({
      globepos_category_variant_weights: nextOverrides,
    });

    if (result && typeof result.then === "function") {
      setSaving(true);
      result
        .then(() => {
          setSaving(false);
          closeEditor();
        })
        .catch((err) => {
          setSaving(false);
          setSaveError(
            (err && err.message) ||
              "An error occurred while saving.  Please try again.",
          );
        });
    } else {
      closeEditor();
    }
  };

  // ---- Remove flow ---------------------------------------------------------
  const handleRemove = (key) => {
    setConfirmingDeleteKey(key);
  };

  const closeRemoveDialog = () => {
    if (saving) return;
    setConfirmingDeleteKey(null);
  };

  const confirmRemove = () => {
    if (!confirmingDeleteKey) return;
    const nextOverrides = { ...overrides };
    delete nextOverrides[confirmingDeleteKey];
    const result = onSave({
      globepos_category_variant_weights: nextOverrides,
    });
    if (result && typeof result.then === "function") {
      setSaving(true);
      result
        .then(() => {
          setSaving(false);
          setConfirmingDeleteKey(null);
        })
        .catch(() => {
          setSaving(false);
        });
    } else {
      setConfirmingDeleteKey(null);
    }
  };

  // ---- Render helpers ------------------------------------------------------
  const renderEntry = (entry) => {
    const descriptorLabel =
      entry.descriptors.length > 0
        ? entry.descriptors
            .map((d) =>
              d.description ? `${d.weight} - ${d.description}` : d.weight,
            )
            .join(", ")
        : "No weight tiers";

    return (
      <ResourceItem
        id={entry.key}
        accessibilityLabel={`Edit ${entry.label}`}
      >
        <Stack alignment="center" distribution="equalSpacing" wrap={false}>
          <Stack.Item fill>
            <Stack vertical spacing="extraTight">
              <TextStyle variation="strong">{entry.label}</TextStyle>
              <TextStyle variation="subdued">
                Category: {entry.key}
                {entry.code ? ` (${entry.code})` : ""}
                {entry.parent_subdepartment
                  ? ` · under ${entry.parent_subdepartment}`
                  : ""}
              </TextStyle>
              <TextStyle variation="subdued">{descriptorLabel}</TextStyle>
            </Stack>
          </Stack.Item>
          <Stack spacing="tight">
            <Button size="slim" onClick={() => handleEditEntry(entry)}>
              Edit
            </Button>
            <Button
              size="slim"
              destructive
              onClick={() => handleRemove(entry.key)}
            >
              Remove
            </Button>
          </Stack>
        </Stack>
      </ResourceItem>
    );
  };

  return (
    <>
      <Card>
        <Card.Header
          title="Category defaults"
          actions={[
            {
              content: "Add category override",
              onAction: () => setPickerOpen(true),
            },
          ]}
        />
        <Card.Section>
          <TextStyle variation="subdued">
            Defaults applied to weighted items matching this CATEGORY
            DESCRIPTION during import.  Per-item PLU overrides still win.
          </TextStyle>
        </Card.Section>
        {entries.length === 0 ? (
          <Card.Section>
            <TextStyle variation="subdued">
              No category overrides configured.
            </TextStyle>
          </Card.Section>
        ) : (
          <ResourceList
            resourceName={{
              singular: "category override",
              plural: "category overrides",
            }}
            items={entries}
            renderItem={renderEntry}
          />
        )}
      </Card>

      <ImportGroupingPickerModal
        open={pickerOpen}
        mode="category"
        shopId={shopId}
        onClose={() => setPickerOpen(false)}
        onSelect={handlePickerSelect}
        existingKeys={Object.keys(overrides)}
      />

      <Modal
        large
        open={Boolean(activeEntry)}
        title={
          activeEntry && activeEntry.isNew
            ? "Add category override"
            : "Edit category override"
        }
        onClose={closeEditor}
        primaryAction={{
          content: "Save",
          onAction: handleSaveEditor,
          loading: saving,
          disabled: !canSave,
        }}
        secondaryActions={[{ content: "Cancel", onAction: closeEditor }]}
      >
        <Modal.Section>
          <TextField
            label="Label"
            value={editorLabel}
            onChange={setEditorLabel}
            autoComplete="off"
            error={
              editorKeyIsBlank
                ? "Label is required (it becomes the category match key)."
                : undefined
            }
            helpText="Matched against CATEGORY DESCRIPTION during GlobePOS import. Stored uppercase."
          />
        </Modal.Section>

        <Modal.Section>
          <TextField
            label="Parent sub-department"
            value={editorParent}
            onChange={setEditorParent}
            autoComplete="off"
            placeholder="Optional (e.g. CAFE)"
            helpText="Display hint only — not used to match in v1.  Leave blank unless the category should be visually grouped under a specific sub-department."
          />
        </Modal.Section>

        <Modal.Section>
          <Stack vertical spacing="loose">
            <TextStyle variation="strong">Weight tiers</TextStyle>
            <WeightDescriptorEditor
              descriptors={editorDescriptors}
              onChange={setEditorDescriptors}
            />
          </Stack>
        </Modal.Section>

        {saveError && (
          <Modal.Section>
            <Banner status="critical">{saveError}</Banner>
          </Modal.Section>
        )}
      </Modal>

      <Modal
        open={Boolean(confirmingDeleteKey)}
        title="Remove category override"
        onClose={closeRemoveDialog}
        primaryAction={{
          content: "Remove override",
          destructive: true,
          onAction: confirmRemove,
          loading: saving,
        }}
        secondaryActions={[{ content: "Cancel", onAction: closeRemoveDialog }]}
      >
        <Modal.Section>
          <TextStyle>
            This category will use the next applicable tier on the next import.
          </TextStyle>
        </Modal.Section>
      </Modal>
    </>
  );
}
